import { Component, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AgentChatStore } from 'src/app/services/agent-chat/agent-chat-store.service';
import { AgentChatHistoryComponent } from '../history/agent-chat-history.component';
import { MatIconModule } from '@angular/material/icon';
import { AgentMessageListComponent, AgentMessageRating } from '../message-list/agent-message-list.component';
import { AGENT_CHAT_STARTERS } from '../agent-chat-starters';

/** The API's refusal codes that have their own message; anything else reads as "something went wrong". */
const FAILURE_KEYS: Record<string, string> = {
  AGENT_RATE_LIMITED: 'agent-chat.error.rate-limited',
  AGENT_DAILY_CAP_REACHED: 'agent-chat.error.daily-cap',
  AGENT_DISABLED: 'agent-chat.error.disabled',
  AGENT_REPLY_FAILED: 'agent-chat.error.reply-failed',
};

/**
 * The support chat's panel (GOO-457): the conversation and its composer, or the history. It shows
 * when the conversation waits for the Goodchart team, and when the agent is off (GOO-458).
 */
@Component({
  selector: 'app-agent-chat-panel',
  standalone: true,
  imports: [
    FormsModule,
    TextFieldModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    AgentChatHistoryComponent,
    AgentMessageListComponent,
  ],
  templateUrl: './agent-chat-panel.component.html',
})
export class AgentChatPanelComponent {
  readonly store = inject(AgentChatStore);
  private readonly translate = inject(TranslateService);

  readonly draft = signal('');
  readonly starters = AGENT_CHAT_STARTERS;

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  constructor() {
    // Keep the newest message, and the reply as it streams, in view.
    effect(() => {
      this.store.messages();
      this.store.pending();

      const element = this.scroller()?.nativeElement;

      if (element) queueMicrotask(() => (element.scrollTop = element.scrollHeight));
    });
  }

  failureKey(code: string) {
    return FAILURE_KEYS[code] ?? 'agent-chat.error.unknown';
  }

  /** A starter asks its question in the merchant's language. */
  ask(starter: string) {
    this.store.send(this.translate.instant(starter) as string);
  }

  rate({ messageId, rating, comment }: AgentMessageRating) {
    this.store.rate(messageId, rating, comment);
  }

  send() {
    if (!this.draft().trim() || this.store.sending() || !this.store.canSend()) return;

    this.store.send(this.draft());
    this.draft.set('');
  }

  /** Enter sends; Shift+Enter starts a new line. */
  onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

    event.preventDefault();
    this.send();
  }
}
